import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  personalBests,
  sportSummaries,
  workoutPointsForSport,
  type SportSummary,
  type WorkoutPoint,
} from '@/db/workout-queries'

type Metric = 'strain' | 'durationMin' | 'distanceKm' | 'paceMinPerKm' | 'avgHr' | 'kj'

const METRIC_DEFS: Record<Metric, { label: string; color: string; unit: string; lowerIsBetter?: boolean }> = {
  strain: { label: 'Strain', color: '#0ea5e9', unit: '' },
  durationMin: { label: 'Duration', color: '#a855f7', unit: 'min' },
  distanceKm: { label: 'Distance', color: '#22c55e', unit: 'km' },
  paceMinPerKm: { label: 'Pace', color: '#eab308', unit: 'min/km', lowerIsBetter: true },
  avgHr: { label: 'Avg HR', color: '#ef4444', unit: 'bpm' },
  kj: { label: 'Energy', color: '#6B7053', unit: 'kJ' },
}

export default function Workouts() {
  const [sports, setSports] = useState<SportSummary[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [points, setPoints] = useState<WorkoutPoint[]>([])
  const [metric, setMetric] = useState<Metric>('strain')

  useEffect(() => {
    sportSummaries().then((s) => {
      setSports(s)
      if (!selected && s.length) setSelected(s[0].sport)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selected) workoutPointsForSport(selected).then(setPoints)
  }, [selected])

  const summary = sports.find((s) => s.sport === selected) ?? null
  const pbs = useMemo(() => personalBests(points), [points])

  const availableMetrics = useMemo<Metric[]>(() => {
    const m: Metric[] = ['strain', 'durationMin']
    if (points.some((p) => p.distanceKm != null)) m.push('distanceKm', 'paceMinPerKm')
    if (points.some((p) => p.avgHr != null)) m.push('avgHr')
    if (points.some((p) => p.kj != null)) m.push('kj')
    return m
  }, [points])

  // Auto-fall-back if currently-selected metric isn't available for this sport.
  useEffect(() => {
    if (!availableMetrics.includes(metric)) setMetric(availableMetrics[0])
  }, [availableMetrics, metric])

  if (!sports.length) {
    return (
      <div className="text-center text-stone-500 py-20">
        <p>No workouts synced yet.</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Workouts</h1>
        <p className="text-sm text-stone-500 mt-1">
          {sports.length} sports · {sports.reduce((a, s) => a + s.count, 0)} total sessions
        </p>
      </div>

      {/* Sport selector */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {sports.map((s) => (
          <SportCard
            key={s.sport}
            summary={s}
            active={s.sport === selected}
            onClick={() => setSelected(s.sport)}
          />
        ))}
      </div>

      {/* Detail */}
      {summary && (
        <div className="space-y-6">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="text-3xl font-semibold">{summary.sport}</h2>
            <div className="text-sm text-stone-500">
              Last on {summary.lastDay ?? '—'}
            </div>
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Sessions" value={summary.count.toString()} />
            <Tile label="Total time" value={formatHours(summary.totalDurationMs)} />
            <Tile
              label="Total strain"
              value={summary.totalStrain ? summary.totalStrain.toFixed(0) : '—'}
              tone="strain"
            />
            <Tile
              label={summary.isCardio ? 'Total distance' : 'Total energy'}
              value={
                summary.isCardio && summary.totalDistanceM
                  ? `${(summary.totalDistanceM / 1000).toFixed(1)} km`
                  : summary.totalKj
                  ? `${Math.round(summary.totalKj).toLocaleString()} kJ`
                  : '—'
              }
            />
          </div>

          {/* Personal records */}
          <div className="rounded-xl border border-stone-200 bg-white shadow-sm p-5">
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">
              Personal records
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
              <PR label="Hardest (strain)" value={pbs.hardest?.value.toFixed(1)} day={pbs.hardest?.day} />
              <PR label="Longest" value={pbs.longest ? `${pbs.longest.value} min` : null} day={pbs.longest?.day} />
              {summary.isCardio && (
                <>
                  <PR
                    label="Furthest"
                    value={pbs.furthest ? `${pbs.furthest.value.toFixed(2)} km` : null}
                    day={pbs.furthest?.day}
                  />
                  <PR
                    label="Fastest pace"
                    value={pbs.fastestPace ? formatPace(pbs.fastestPace.value) : null}
                    day={pbs.fastestPace?.day}
                  />
                </>
              )}
              {!summary.isCardio && (
                <PR
                  label="Highest avg HR"
                  value={pbs.highestAvgHr ? `${pbs.highestAvgHr.value} bpm` : null}
                  day={pbs.highestAvgHr?.day}
                />
              )}
            </div>
          </div>

          {/* Trend chart */}
          <div className="rounded-xl border border-stone-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="text-xs uppercase tracking-wider text-stone-500">
                Progression over time
              </div>
              <div className="flex flex-wrap gap-1">
                {availableMetrics.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMetric(m)}
                    className={clsx(
                      'px-3 py-1 text-xs rounded-md transition-colors',
                      metric === m
                        ? 'bg-brand/25 text-brand-fg font-medium'
                        : 'text-stone-600 hover:bg-stone-100',
                    )}
                  >
                    {METRIC_DEFS[m].label}
                  </button>
                ))}
              </div>
            </div>
            <EfficiencySummary points={points} metric={metric} />
            <TrendChart points={points} metric={metric} sparse={summary.count < 12} />
          </div>

          {/* Recent sessions */}
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">
              Recent sessions
            </div>
            <div className="space-y-2">
              {[...points]
                .reverse()
                .slice(0, 12)
                .map((p) => (
                  <SessionRow key={p.start} point={p} isCardio={summary.isCardio} />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SportCard({
  summary,
  active,
  onClick,
}: {
  summary: SportSummary
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left rounded-xl border p-4 transition-colors',
        active
          ? 'border-brand bg-brand/10'
          : 'border-stone-200 bg-white shadow-sm hover:border-stone-300 hover:bg-stone-50',
      )}
    >
      <div className="text-base font-medium truncate">{summary.sport}</div>
      <div className="text-xs text-stone-500 mt-1">
        {summary.count} session{summary.count === 1 ? '' : 's'} · {formatHours(summary.totalDurationMs)}
      </div>
      {summary.lastDay && (
        <div className="text-[11px] text-stone-400 mt-1">last {summary.lastDay}</div>
      )}
    </button>
  )
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'strain' }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white shadow-sm p-4">
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div
        className={clsx(
          'mt-1.5 text-2xl font-semibold tabular-nums',
          tone === 'strain' ? 'text-whoop-strain' : 'text-stone-900',
        )}
      >
        {value}
      </div>
    </div>
  )
}

function PR({ label, value, day }: { label: string; value?: string | null; day?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-base font-medium tabular-nums">{value ?? '—'}</div>
      {day && <div className="text-[10px] text-stone-400">{day}</div>}
    </div>
  )
}

function SessionRow({ point, isCardio }: { point: WorkoutPoint; isCardio: boolean }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium">{point.day}</div>
        <div className="text-xs text-stone-500">
          {point.durationMin} min
          {point.distanceKm != null && ` · ${point.distanceKm.toFixed(2)} km`}
          {point.paceMinPerKm != null && ` · ${formatPace(point.paceMinPerKm)} pace`}
          {!isCardio && point.kj != null && ` · ${Math.round(point.kj)} kJ`}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {point.avgHr != null && (
          <div className="text-xs text-stone-500 text-right">
            <div>
              {point.avgHr}
              {point.maxHr ? <span className="text-stone-400">/{point.maxHr}</span> : ''}
            </div>
            <div className="text-[10px] text-stone-400">avg/max bpm</div>
          </div>
        )}
        {point.strain != null && (
          <div className="px-2.5 py-1 rounded-md bg-sky-50 border border-sky-200 text-sky-700 tabular-nums text-sm font-medium">
            {point.strain.toFixed(1)}
          </div>
        )}
      </div>
    </div>
  )
}

function EfficiencySummary({
  points,
  metric,
}: {
  points: WorkoutPoint[]
  metric: Metric
}) {
  const def = METRIC_DEFS[metric]
  const values = points
    .map((p) => p[metric])
    .filter((v): v is number => typeof v === 'number')

  if (values.length < 6) {
    return null // not enough data to call out a trend
  }

  // Compare first third to last third (more robust than first/last point).
  const slice = Math.max(3, Math.floor(values.length / 3))
  const first = values.slice(0, slice)
  const last = values.slice(-slice)
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length
  const fromAvg = avg(first)
  const toAvg = avg(last)
  if (!Number.isFinite(fromAvg) || !Number.isFinite(toAvg)) return null

  const diff = toAvg - fromAvg
  const pct = (diff / fromAvg) * 100
  const improved = def.lowerIsBetter ? diff < 0 : diff > 0
  const same = Math.abs(pct) < 1.5

  // Pace formatting needs special handling
  const formatVal = (v: number) =>
    metric === 'paceMinPerKm' ? formatPace(v) : `${formatMetric(metric, v)}${def.unit ? ' ' + def.unit : ''}`

  const arrow = same ? '→' : improved ? '↑' : '↓'
  const verbalChange = same
    ? 'roughly the same'
    : improved
    ? `${Math.abs(pct).toFixed(0)}% better`
    : `${Math.abs(pct).toFixed(0)}% slower than your earlier sessions`

  return (
    <div className="mb-3 text-sm flex items-baseline gap-2 flex-wrap">
      <span className="tabular-nums text-stone-500">
        {formatVal(fromAvg)} {arrow} {formatVal(toAvg)}
      </span>
      <span
        className={clsx(
          'text-xs',
          same
            ? 'text-stone-500'
            : improved
            ? 'text-green-600'
            : 'text-stone-600',
        )}
      >
        {def.label} is {verbalChange}.
      </span>
    </div>
  )
}

function TrendChart({
  points,
  metric,
  sparse,
}: {
  points: WorkoutPoint[]
  metric: Metric
  sparse: boolean
}) {
  const def = METRIC_DEFS[metric]
  const data = points
    .map((p) => {
      const v = p[metric]
      return typeof v === 'number' ? { day: p.day, value: v } : null
    })
    .filter((x): x is { day: string; value: number } => !!x)

  if (data.length === 0) {
    return (
      <div className="h-[260px] flex items-center justify-center text-sm text-stone-500">
        No {def.label.toLowerCase()} data for this sport.
      </div>
    )
  }

  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        {sparse ? (
          <ScatterChart margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgb(231 229 228)" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="rgb(168 162 158)" />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="rgb(168 162 158)"
              width={40}
              reversed={!!def.lowerIsBetter}
            />
            <Tooltip
              contentStyle={chartTooltipStyle}
              formatter={(v: number) => `${formatMetric(metric, v)}${def.unit ? ' ' + def.unit : ''}`}
            />
            <Scatter data={data} fill={def.color} />
          </ScatterChart>
        ) : (
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgb(231 229 228)" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="rgb(168 162 158)" />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="rgb(168 162 158)"
              width={40}
              reversed={!!def.lowerIsBetter}
            />
            <Tooltip
              contentStyle={chartTooltipStyle}
              formatter={(v: number) => `${formatMetric(metric, v)}${def.unit ? ' ' + def.unit : ''}`}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={def.color}
              strokeWidth={1.75}
              dot={{ r: 2.5, fill: def.color }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}

const chartTooltipStyle = {
  background: 'rgb(255 255 255)',
  border: '1px solid rgb(214 211 209)',
  borderRadius: 8,
  fontSize: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
}

function formatMetric(metric: Metric, v: number): string {
  if (metric === 'paceMinPerKm') return formatPace(v)
  if (metric === 'distanceKm') return v.toFixed(2)
  if (metric === 'strain') return v.toFixed(1)
  if (metric === 'kj') return Math.round(v).toString()
  return v.toFixed(0)
}

function formatPace(minPerKm: number): string {
  const min = Math.floor(minPerKm)
  const sec = Math.round((minPerKm - min) * 60)
  return `${min}:${String(sec).padStart(2, '0')}`
}

function formatHours(ms: number): string {
  if (!ms) return '0h'
  const totalMin = Math.round(ms / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}
